import { type InfiniteData } from '@tanstack/react-query';
import produce from 'immer';
import { chunk, constant } from 'lodash';

import { type ClipsListResponse } from '@air/api';
import {
  type Asset,
  type Board,
  type Clip,
  type ClipAndBoardListItem,
  type ListResponse,
  SortDirection,
  type SubnavBoardSort,
} from '@air/api/types';

import { getUpdatedBoard } from '~/utils/BoardUtils';
import { getOrderedBoards } from '~/utils/mutateUtils/getOrderedBoards';
import { isClipTableItem } from '~/utils/tableViewDataUtils';

export type UpdatedBoardBase = Pick<Board, 'id'> & Partial<Board>;
export type UpdatedBoard = UpdatedBoardBase & Pick<Board, 'parentId' | 'hasCurrentUser' | 'workspaceId'>;

export const defaultBoardsState: InfiniteData<ListResponse<Board>> = {
  pages: [
    {
      data: [],
      total: 0,
      pagination: {
        hasMore: false,
        cursor: null,
      },
    },
  ],
  pageParams: [],
};

export const getUpdatedBoards =
  (updatedBoard: UpdatedBoardBase, boardSort: SubnavBoardSort = { name: '', direction: SortDirection.desc }) =>
  (data?: InfiniteData<ListResponse<Board>>): InfiniteData<ListResponse<Board>> | undefined => {
    if (!data) {
      return;
    }

    return produce(data, (draft) => {
      data.pages.forEach(() => {
        const flattenedBoards = draft.pages.flatMap((b) => b.data);
        const updatedBoardIndex = flattenedBoards.findIndex((b) => b.id === updatedBoard.id);

        if (updatedBoardIndex === -1) {
          return;
        }

        flattenedBoards[updatedBoardIndex] = getUpdatedBoard({
          newBoard: updatedBoard,
          oldBoard: flattenedBoards[updatedBoardIndex],
        });

        const { orderedBoards } = getOrderedBoards(flattenedBoards, boardSort);
        const chunkedBoards = chunk(orderedBoards, Math.ceil(orderedBoards.length / draft?.pages.length));

        chunkedBoards.forEach((chunk, index) => {
          draft.pages[index].data = chunk;
        });
      });
    });
  };

export const getRearrangedBoards =
  (rearrangedBoards: Board[], insertAtIndex?: number) =>
  (data?: InfiniteData<ListResponse<Board>>): InfiniteData<ListResponse<Board>> | undefined => {
    if (!data) {
      return;
    }

    return produce(data, (draft) => {
      draft.pages.forEach(() => {
        const rearrangedBoardsIds = new Set(rearrangedBoards.map(({ id }) => id));
        const flattenedBoards = draft.pages.flatMap((b) => b.data);
        const remaining = flattenedBoards.filter((b) => !rearrangedBoardsIds.has(b.id));

        // `insertAtIndex` is the start index of the moved block in the
        // optimistic full list (`updatedBoards` from `useGalleryRearrange`).
        // Callers derive this via `updatedBoards.findIndex` on the first moved
        // board — it may differ from `calculateRearrangeData`'s `newIndex` when
        // multiple boards are selected. The splice index in `remaining` equals
        // that value clamped to the cache bounds. Without an index, append.
        let finalIndex = remaining.length;

        if (insertAtIndex !== undefined) {
          finalIndex = Math.min(Math.max(insertAtIndex, 0), remaining.length);
        }

        remaining.splice(finalIndex, 0, ...rearrangedBoards);

        const chunkedBoards = chunk(remaining, Math.ceil(remaining.length / draft.pages.length));
        chunkedBoards.forEach((chunk, index) => {
          if (draft.pages[index]) {
            draft.pages[index].data = chunk;
          }
        });
      });
    });
  };

export const mergeReorderedSiblingsIntoFlatBoards = (flatBoards: Board[], reorderedSiblings: Board[]): Board[] => {
  const siblingIds = new Set(reorderedSiblings.map(({ id }) => id));
  const withoutSiblings = flatBoards.filter((b) => !siblingIds.has(b.id));
  const anchorIndex = flatBoards.findIndex((b) => siblingIds.has(b.id));

  if (anchorIndex === -1) {
    return [...withoutSiblings, ...reorderedSiblings];
  }

  return [...withoutSiblings.slice(0, anchorIndex), ...reorderedSiblings, ...withoutSiblings.slice(anchorIndex)];
};

export const getPersonalBoardBetweenIds = (
  flatBoards: Board[],
  insertAtIndex: number,
  movedCount: number,
): { moveAfterId?: string; moveBeforeId?: string } => {
  if (insertAtIndex < 0) {
    return {};
  }

  const moveAfterId = insertAtIndex > 0 ? flatBoards[insertAtIndex - 1]?.id : undefined;
  const moveBeforeId =
    insertAtIndex + movedCount < flatBoards.length ? flatBoards[insertAtIndex + movedCount]?.id : undefined;

  return { moveAfterId, moveBeforeId };
};

export const mergeReorderedSiblingsIntoFlatList =
  (reorderedSiblings: Board[]) =>
  (data?: InfiniteData<ListResponse<Board>>): InfiniteData<ListResponse<Board>> | undefined => {
    if (!data) {
      return;
    }

    return produce(data, (draft) => {
      draft.pages.forEach(() => {
        const flattenedBoards = draft.pages.flatMap((b) => b.data);
        const merged = mergeReorderedSiblingsIntoFlatBoards(flattenedBoards, reorderedSiblings);

        const chunkedBoards = chunk(merged, Math.ceil(merged.length / draft.pages.length));
        chunkedBoards.forEach((chunk, index) => {
          if (draft.pages[index]) {
            draft.pages[index].data = chunk;
          }
        });
      });
    });
  };

export type GetGalleryDataWithoutIdsParams = {
  data?: ClipsListResponse<Clip>[];
  removeIf?: (clip: Clip) => boolean;
} & (
  | {
      clipIds: Clip['id'][];
      assetIds?: never;
    }
  | {
      clipIds?: never;
      assetIds: Asset['id'][];
    }
);

export const getGalleryDataWithoutIds = ({
  assetIds,
  clipIds,
  data,
  removeIf = constant(false),
}: GetGalleryDataWithoutIdsParams) => {
  let totalRemoved = 0;

  const newData = data?.reduce<ClipsListResponse<Clip>[]>((acc, curr) => {
    const newClips = curr.data.clips.filter((item) => {
      if (clipIds?.includes(item.id) || assetIds?.includes(item.assetId) || removeIf(item)) {
        return false;
      }

      return true;
    });
    totalRemoved += curr.data.clips.length - newClips.length;

    acc.push({
      ...curr,
      data: {
        ...curr.data,
        clips: newClips,
      },
    });
    return acc;
  }, []);

  return produce(newData, (draft) => {
    draft?.forEach((data) => {
      data.data.total = data.data.total - totalRemoved;
    });
  });
};

export type GetTableDataWithoutIdsParams = {
  data?: ListResponse<ClipAndBoardListItem>[];
  removeIf?: (clip: Clip) => boolean;
} & (
  | {
      clipIds: Clip['id'][];
      assetIds?: never;
    }
  | {
      clipIds?: never;
      assetIds: Asset['id'][];
    }
);

export const getTableDataWithoutIds = ({
  assetIds,
  clipIds,
  data,
  removeIf = constant(false),
}: GetTableDataWithoutIdsParams) => {
  let totalRemoved = 0;

  const newData = data?.reduce<ListResponse<ClipAndBoardListItem>[]>((acc, curr) => {
    const newClips = curr.data.filter((item) => {
      if (item.type === 'board') {
        return true;
      }

      if (!isClipTableItem(item)) {
        return true;
      }

      const clipData = item.data;

      if (clipIds?.includes(clipData.id) || assetIds?.includes(clipData.assetId) || removeIf?.(clipData)) {
        return false;
      }

      return true;
    });

    totalRemoved += curr.data.length - newClips.length;

    acc.push({
      ...curr,
      data: newClips,
    });

    return acc;
  }, []);

  return produce(newData, (draft) => {
    draft?.forEach((data) => {
      data.total = data.total - totalRemoved;
    });
  });
};

/** Keep customFields. Count from version number, or old+1 when the API omits it. */
export const standardNewVersionOverride = (oldClip: Clip, newClip: Clip) => ({
  ...newClip,
  customFields: oldClip.customFields,
  assetVersionCount: newClip.version ?? (oldClip.assetVersionCount ?? 0) + (oldClip.id === newClip.id ? 0 : 1),
});

export const updateVersionInTableData = (data: ListResponse<ClipAndBoardListItem>[], newVersion: Clip) =>
  produce(data, (draft) => {
    draft.forEach((data) => {
      const itemIndex = data.data.findIndex((item) => {
        if (isClipTableItem(item)) {
          return item.data.assetId === newVersion.assetId;
        }

        return false;
      });

      if (itemIndex !== -1) {
        const item = data.data[itemIndex];

        if (isClipTableItem(item)) {
          data.data[itemIndex].data = standardNewVersionOverride(item.data, newVersion);
        }
      }
    });
  });
